import { useEffect } from 'react'
import {
  knowledgeApi,
  type Belief,
  type CustomerKnowledge as CustomerKnowledgeData,
  type KnowledgePerson,
} from '../../services/api'
import BeliefRows, { useBeliefRead, type BeliefRowsState, type BeliefSubject } from './BeliefRows'
import { refusalFor } from './refusal'

/**
 * What Marva believes about one person, and why (App IA v5 phase 5b, MAR-204).
 *
 * ── Beliefs only, and why the record is NOT here ────────────────────────────────────────
 *
 * The API answers two kinds of thing and only one of them can be acted on. **What she
 * believes** is a theory she formed from evidence, and the merchant can tell her she is
 * wrong about it — that is this component. **What she has read** is a fact off the record,
 * with no write path anywhere in the platform; it is rendered by the drawer in `KnowledgeDrawer.tsx`
 * from the record the drawer already holds. Two reasons, and the second is the load-bearing
 * one:
 *
 *  1. A uniform list would put a control on rows that cannot take one, which is a promise
 *     the surface would break on the first click.
 *  2. ⚠️ **This read must not become a single point of failure for data it does not
 *     source.** `person` (`knowledge-api/src/reads.js`) does not carry
 *     `behavioralInsights.decisionDrivers`, `predictedNeeds` or `shoppingPsychology` at
 *     all — so a customer with `shoppingPsychology` and no `synthesizedDescription` simply
 *     lost their description — and the fields it DOES carry (orders/lifetime, `segments`,
 *     `synthesizedDescription`) are already loaded in the drawer.
 *     Rendering them from here meant a knowledge-api 500, or merely the latency before this
 *     read lands, blanked numbers this route never fetched. One source per fact, and the
 *     source is the one that already has it.
 *
 * ── A thin adapter (MAR-326) ────────────────────────────────────────────────────────────
 *
 * The read, the verdict, the in-flight set, the counts and the fact rows are `BeliefRows`,
 * shared with `ProductKnowledge` so the two subjects cannot drift. What is left here is
 * what is true of a PERSON alone: the route, the id field (`hypothesisId`), and the copy.
 *
 * ── Three read states, and the one that must not be silent ──────────────────────────────
 *
 * A failed belief read is a **500**, never an empty `beliefs` array: the server calls
 * `fetchHypotheses(..., { rethrow: true })` for exactly that reason. So "loading",
 * "could not read" and "she believes nothing yet" are three distinct renders here and
 * never collapse into one. Reporting "nothing" when the answer is "we could not find out"
 * is the quiet failure this repo keeps being bitten by, and this is the one surface whose
 * whole claim is the opposite.
 *
 * ── `person` goes UP, for two consumers ───────────────────────────────────────────────────
 *
 * `onPerson` hands the drawer the read's `person`: the erasure section needs its
 * `emailMasked` (§9.1 masks rather than redacts it *because the erasure confirmation has to
 * name a person*, and nothing else carries the mask), and a cold deep link's header falls
 * back to its name. ⚠️ It reports `null` while loading and on a failed read, and the drawer
 * keeps erasure mounted either way — a belief read that 500s must not cost the merchant the
 * floor-claim control (see `KnowledgeDrawer.tsx`).
 */

/** The person rail. Module-level: its identity is a hook dependency. */
const CUSTOMER_SUBJECT: BeliefSubject<Belief, CustomerKnowledgeData> = {
  idOf: (b) => b.hypothesisId,
  read: async(ws, id, opts) => {
    const res = await knowledgeApi.getCustomerKnowledge(ws, id, opts)
    if (res.success && res.data) return { kind: 'read', data: res.data }
    return { kind: 'failed', refusal: refusalFor(res) }
  },
  settle: (ws, id, beliefId, body) => knowledgeApi.settleBelief(ws, id, beliefId, body),
  beliefsOf: (d) => ({ beliefs: d.beliefs, counts: d.counts }),
  withBeliefs: (d, beliefs, counts) => ({ ...d, beliefs, counts }),
}

export default function CustomerKnowledge({
  workspaceId,
  customerId,
  onPerson,
}: {
  workspaceId?: string
  customerId?: string
  /** The read's `person` once it lands for THIS record, else `null`. Scoped by the caller. */
  onPerson?: (workspaceId: string, customerId: string, person: KnowledgePerson | null) => void
}) {
  const read = useBeliefRead(CUSTOMER_SUBJECT, workspaceId, customerId)
  const { current } = read
  // From the last SUCCESSFUL read, not `current`: `person` does not change with the settled
  // toggle, so the erasure confirmation and a cold link's header must not lose the name for
  // the frame the wider read is in flight — nor for good when that wider read FAILS.
  const person = read.lastRead?.data?.person ?? null

  useEffect(() => {
    if (workspaceId && customerId) onPerson?.(workspaceId, customerId, person)
  }, [onPerson, workspaceId, customerId, person])

  if (!workspaceId || !customerId) {
    // A row with no id is not a record this API can be asked about, and a request built
    // from a blank id would address the workspace's whole customer path.
    return null
  }

  const state: BeliefRowsState<Belief> = !current
    ? { kind: 'loading' }
    : current.failed || !current.data
      ? {
          kind: 'failed',
          // ⚠️ A failed belief read is a 500, never `beliefs: []` — the server says so
          //    precisely so this can be said out loud.
          text:
            current.refusal ||
            'Could not read what she believes, so nothing is shown rather than a guess. Reload to try again.',
        }
      : { kind: 'ready', beliefs: current.data.beliefs, counts: current.data.counts }

  return (
    <BeliefRows
      read={read}
      state={state}
      loadingText="Reading what she believes…"
      noneText="She has not formed a theory about this person yet. Theories come from what they do, so there is nothing here until there is evidence for something."
    />
  )
}

